import React from 'react';
import classNames from 'classnames';
import { withRouter } from 'next/router';
import PropTypes from 'prop-types';
import styles from './Menu-tw-styles';
import { Button } from '../../Generic/Button';
import MenuItem from './MenuItem';
import i18nMessages from '../../../constants/messages/pt-BR';
import { handleFocusTrap } from '../../../utils/ariaHiddenHelper';

const normalizeForMatch = value => {
  const text = String(value || '')
    .trim()
    .toLowerCase();
  if (typeof text.normalize === 'function') {
    return text.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  }
  return text;
};

const getMenuSectionAriaLabel = (menuItem, menuText) => {
  const normalizedText = normalizeForMatch(menuText);
  const normalizedLink = normalizeForMatch(menuItem && menuItem.menuLink);

  if (
    normalizedText.includes('produto') ||
    normalizedLink.includes('/produtos') ||
    normalizedLink.includes('/colecoes')
  ) {
    return 'Menu: seção de produtos e coleções';
  }
  if (
    normalizedText.includes('ingrediente') ||
    normalizedLink.includes('ingrediente')
  ) {
    return 'Menu: seção de ingredientes';
  }
  if (
    normalizedText.includes('sobre') ||
    normalizedText.includes('nossa historia') ||
    normalizedText.includes('manifesto') ||
    normalizedLink.includes('manifest') ||
    normalizedLink.includes('historia')
  ) {
    return 'Menu Sobre Nós';
  }

  return `Menu: seção de ${menuText}`;
};

export class MenuComponent extends React.Component {
  constructor() {
    super();
    this.state = {
      activeSlotIndex: -1,
    };
    this.menuDropdownRef = React.createRef();
  }
  componentDidUpdate(nextProps) {
    const { activeSlotIndexProp, activeIndex, menuIndex } = this.props;
    if (activeSlotIndexProp !== nextProps.activeSlotIndexProp) {
      this.updateStateOnProps(activeSlotIndexProp);
    }

    // Setup focus trap when menu becomes active (desktop)
    if (activeIndex === menuIndex && activeIndex !== nextProps.activeIndex) {
      this.setupFocusTrap();
    } else if (
      activeIndex !== menuIndex &&
      activeIndex === nextProps.activeIndex
    ) {
      this.removeFocusTrap();
    }
  }

  componentWillUnmount() {
    this.removeFocusTrap();
  }

  setupFocusTrap = () => {
    const isMobile = window.innerWidth < 1024;

    // Only setup keyboard event listener on desktop
    if (!isMobile) {
      document.addEventListener('keydown', this.handleKeyDown);
    }

    // Focus the close button first (for both mobile and desktop)
    // Delay increased to 400ms to allow screen readers to announce button state change before focus moves
    if (this.menuDropdownRef.current) {
      setTimeout(() => {
        // Try to find and focus the close button
        const closeButton = this.menuDropdownRef.current.querySelector('.header-dropdown-close');
        if (closeButton && typeof closeButton.focus === 'function') {
          closeButton.focus();
        } else {
          // Fallback: focus the first focusable element
          const focusableElements = this.menuDropdownRef.current.querySelectorAll(
            'button:not([disabled]), [href]:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"]):not([disabled])'
          );
          if (focusableElements.length > 0) {
            focusableElements[0].focus();
          }
        }
      }, 400);
    }
  };

  removeFocusTrap = () => {
    document.removeEventListener('keydown', this.handleKeyDown);
  };

  handleKeyDown = e => {
    // Handle Escape key to close menu
    if (e.key === 'Escape') {
      const { changeActiveMenu } = this.props;
      e.preventDefault();
      changeActiveMenu(-1);
      return;
    }

    // Handle Tab key for focus trap
    if (this.menuDropdownRef.current) {
      handleFocusTrap(this.menuDropdownRef.current, e);
    }
  };
  updateStateOnProps = value => {
    this.setState({ activeSlotIndex: value });
  };
  changeActiveSlot = index => {
    const { activeSlotIndex } = this.state;
    this.setState({
      activeSlotIndex: activeSlotIndex === index ? -1 : index,
    });
  };
  resetSlot = () => {
    const { menuCloseFn } = this.props;
    this.setState({
      activeSlotIndex: -1,
    });
    menuCloseFn ? menuCloseFn() : {};
  };

  render() {
    const {
      menuItem,
      changeActiveMenu,
      menuIndex,
      activeIndex,
      resetMenu,
      router,
    } = this.props;
    const { activeSlotIndex } = this.state;
    const menuText = menuItem && menuItem.menuText ? menuItem.menuText : 'Menu';
    const ariaLabelLink = `Ir para ${menuText}`;
    
    // Count submenu items
    const submenuCount = menuItem.menuSlotsCollection?.items?.length || 0;
    const submenuText = submenuCount > 0 ? `${submenuCount} ${submenuCount === 1 ? 'item' : 'itens'}` : '';
    
    // Only announce 'selected' when menu is actually expanded (activeIndex === menuIndex)
    const isSelected = activeIndex === menuIndex;
    // For the first menu item, count the number of top-level menu items from menuArray prop
    let totalMenuItems = 1;
    if (menuIndex === 0 && Array.isArray(this.props.menuArray)) {
      totalMenuItems = this.props.menuArray.length;
    }
    const listPrefix = menuIndex === 0 ? `List with ${totalMenuItems} items, ` : '';
    const ariaLabelDesktop = `${listPrefix}${getMenuSectionAriaLabel(menuItem, menuText)}${isSelected ? submenuText : ''} (${isSelected ? 'Seleccionado' : 'No seleccionado'}) (${isSelected ? 'Expandido' : 'Contraído'})`;
    const ariaLabelMobile = `${listPrefix}${getMenuSectionAriaLabel(menuItem, menuText)}${isSelected ? submenuText : ''} (${isSelected ? 'Seleccionado' : 'No seleccionado'}) (${isSelected ? 'Expandido' : 'Contraído'})`;
    return (
      <React.Fragment>
        <li
          className={classNames(
            styles.navlist,
            activeIndex === menuIndex
              ? 'lg:border-b-2 lg:border-solid lg:border-black'
              : 'border-b-2 border-solid border-transparent'
          )}
        >
          {menuItem.menuSlotsCollection &&
          menuItem.menuSlotsCollection.items.length === 0 ? (
            <Button
              to={menuItem.menuLink}
              className={classNames(
                styles.navlistitem,
                'event_menu_click',
                activeIndex === -1 ? '' : 'sm:hidden lg:block',
                router.asPath === menuItem.menuLink ||
                  router.asPath == menuItem.menuLink + '/'
                  ? styles.navlistitemselected
                  : ''
              )}
              onClick={resetMenu}
              data-action-detail={menuItem.menuText}
              aria-label={ariaLabelLink}
            >
              {menuItem.menuText}
            </Button>
          ) : (
            <React.Fragment>
              {/* for desktop */}
              <Button
                className={classNames(
                  styles.navlistitemCollection,
                  'event_menu_click',
                  router.asPath === menuItem.menuLink ||
                    router.asPath == menuItem.menuLink + '/'
                    ? styles.navlistitemselected
                    : ''
                )}
                onClick={() => changeActiveMenu(menuIndex)}
                data-action-detail={menuItem.menuText}
                // aria-expanded={activeIndex === menuIndex}
                aria-haspopup="true"
                aria-label={ariaLabelDesktop}
              >
                <span>{menuItem.menuText}</span>
              </Button>
              {/* for mobile */}
              <Button
                className={classNames(
                  styles.navlistitemMobile,
                  'event_menu_click',
                  router.asPath === menuItem.menuLink ||
                    router.asPath == menuItem.menuLink + '/'
                    ? styles.navlistitemselected
                    : '',
                  activeIndex !== menuIndex && activeIndex === -1
                    ? ''
                    : 'hidden'
                )}
                onClick={() =>
                  changeActiveMenu(menuIndex, i18nMessages.animate)
                }
                data-action-detail={menuItem.menuText}
                // aria-expanded={activeIndex === menuIndex}
                aria-haspopup="true"
                aria-label={ariaLabelMobile}
              >
                {menuItem.menuText}
                <span className={styles.rightArrow} aria-hidden="true" />
              </Button>
            </React.Fragment>
          )}
        </li>
        <div
          ref={this.menuDropdownRef}
          className={
            activeIndex === menuIndex
              ? styles.activeMenuWrapper
              : styles.menuWrapper
          }
          role={activeIndex === menuIndex ? 'menu' : undefined}
          aria-modal={activeIndex === menuIndex ? 'true' : undefined}
          aria-label={
            activeIndex === menuIndex ? `${menuItem.menuText} menu` : undefined
          }
        >
          <div className={styles.menuDropdownContainer}>
            <div className={styles.menuItemHeader}>{menuItem.menuText}</div>
            {/* Accessible close button for desktop menu */}
            {activeIndex === menuIndex && (
              <button
                className={styles.closeButton}
                onClick={() => changeActiveMenu(-1)}
                aria-label="Fechar menu"
                tabIndex={0}
                style={{ position: 'absolute', top: 10, right: 10, zIndex: 10 }}
                type='button'
              >
              <span aria-hidden="true">×</span>
              </button>
            )}
            <ul className={styles.ulMenuList} role="presentation">
              {menuItem.menuSlotsCollection &&
                menuItem.menuSlotsCollection.items.map((menuslot, index) => (
                  <MenuItem
                    key={index}
                    menuSlot={menuslot}
                    changeActiveSlot={this.changeActiveSlot}
                    resetSlot={this.resetSlot}
                    resetMenu={resetMenu}
                    slotIndex={index}
                    activeSlotIndex={activeSlotIndex}
                    changeActiveMenu={changeActiveMenu}
                  />
                ))}
            </ul>
          </div>
        </div>
      </React.Fragment>
    );
  }
}

export default withRouter(MenuComponent);

MenuComponent.propTypes = {
  menuItem: PropTypes.oneOfType([PropTypes.object]),
  menuCloseFn: PropTypes.func.isRequired,
  changeActiveMenu: PropTypes.func.isRequired,
  menuIndex: PropTypes.number.isRequired,
  activeIndex: PropTypes.number,
  resetMenu: PropTypes.func.isRequired,
  router: PropTypes.oneOfType([PropTypes.object]).isRequired,
  activeSlotIndexProp: PropTypes.bool,
};
// TODO(next16): defaultProps removed in React 19 for function components.
// Convert to inline parameter defaults: const MenuComponent = ({prop = val, menuItem = { }) =>
MenuComponent.defaultProps = {
  menuItem: {},
  activeIndex: 0,
  activeSlotIndexProp: false,
};
